import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Eye,
  Edit,
  MoreVertical,
  X,
  Users,
  Shield,
  UserCog,
  XCircle,
  CheckCircle,
  Search,
  Plus,
} from 'lucide-react';
import { useSystem } from '@ohif/core';
import { ICON_STROKE_WIDTH, ROLE_OPTIONS, STATUS_OPTIONS, SUB_ROLE_OPTIONS } from '../../constants';
import PageLayout from '../../components/layouts/PageLayout';
import type { BreadcrumbItem } from '../../components/layouts/PageHeader';
import Button from '../../components/Button';
import { DataTable, Pagination, TableColumn } from '../../components/common';
import FormSelectVirtualized from '../../components/common/FormSelectVirtualized';
import EmptyState from '../../components/common/EmptyState';
import ChangePasswordModal from '../../components/common/ChangePasswordModal';
import userService from '../../services/userService';
import authService from '../../auth/authService';
import {
  mapApiResponseToTableFormat,
  extractFilenameFromPath,
  resolveSignatureAssetUrl,
  formatTimeForDisplay,
} from '../../utils/userMapper';
import SignaturePreviewDialog from '../../components/SignaturePreviewDialog';
import { formatDateTimeShort } from '../../utils/dateFormatter';
import UserFormDialog from '../../components/UserFormDialog';
import type {
  User,
  UserTableRow,
  UserPaginationInfo,
  ActionMenuAnchor,
} from '../../types/user.types';
import { getUserRoleFromAuthUser } from '../../utils/roleUtils';
import { useDebounce } from '../../hooks/useDebounce';
import {
  buildUserManagementBreadcrumbs,
  buildUsersListFilters,
  formatAvailabilityHoursForUserView,
  formatDepartmentForUserView,
  formatEmployeeIdForUserView,
  formatUserSpecialty,
  handleUserFormSuccessFlow,
  isFacilityScopedUsersList,
  notifyUserManagement,
  resolveFacilityIdFromNav,
  resolveFacilityNameFromNav,
  resolveSummaryStats,
  resolveUsersListFacilityId,
  toggleUserActiveStatus,
} from './userManagementHelpers';
import './UserManagement.css';

async function refreshUserSummaryCounts(
  facilityIdParam: string | undefined,
  setSummaryCounts: React.Dispatch<
    React.SetStateAction<{
      total: number;
      radiologists: number;
      technicians: number;
      inactive: number;
      active: number;
    } | null>
  >
): Promise<void> {
  const [totalRes, activeRadRes, activeTechRes, inactiveRes] = await Promise.all([
    userService.getAllUsers(1, facilityIdParam, undefined),
    userService.getAllUsers(1, facilityIdParam, { role: 'Radiologist', status: 'Active' }),
    userService.getAllUsers(1, facilityIdParam, { role: 'Technician', status: 'Active' }),
    userService.getAllUsers(1, facilityIdParam, { status: 'Inactive' }),
  ]);
  if (!totalRes.pagination) {
    return;
  }
  const total = totalRes.pagination.totalUsers ?? 0;
  const radiologists = activeRadRes.pagination?.totalUsers ?? 0;
  const technicians = activeTechRes.pagination?.totalUsers ?? 0;
  const inactive = inactiveRes.pagination?.totalUsers ?? 0;
  setSummaryCounts({
    total,
    radiologists,
    technicians,
    inactive,
    active: total - inactive,
  });
}

function UserManagementViewUserDialogContent(
  props: Readonly<{
    user: User;
    onClose: () => void;
    formatSpecialty: (value: User['specialty']) => string;
    onPreviewSignature: () => void;
    onEditFromView: (user: User) => void;
  }>
) {
  const {
    user: selectedUserForView,
    onClose,
    formatSpecialty,
    onPreviewSignature,
    onEditFromView,
  } = props;
  return (
    <>
      <div
        className="user-management-view-dialog-backdrop"
        role="presentation"
        aria-hidden="true"
      />
      <div
        className="user-management-view-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="view-user-title"
      >
        <div
          className="user-management-view-dialog-content user-details-dialog-content"
          onClick={e => e.stopPropagation()}
        >
          <div className="user-management-view-dialog-header">
            <div>
              <h2
                id="view-user-title"
                className="user-management-view-dialog-title"
              >
                User Details
              </h2>
              <p className="user-details-dialog-subtitle">
                Complete information for {selectedUserForView.full_name}
              </p>
            </div>
            <button
              type="button"
              className="user-management-view-dialog-close"
              onClick={onClose}
              aria-label="Close"
            >
              <X
                className="h-5 w-5"
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </button>
          </div>
          <div className="user-management-view-dialog-body">
            <h3 className="user-details-section-title">General Information</h3>
            <div className="user-details-grid">
              {selectedUserForView.role !== 'ReferringPhysician' && (
                <div className="user-management-view-dialog-row">
                  <span className="user-management-view-dialog-label">Employee ID</span>
                  <span className="user-management-view-dialog-value">
                    {formatEmployeeIdForUserView(selectedUserForView)}
                  </span>
                </div>
              )}
              <div className="user-management-view-dialog-row">
                <span className="user-management-view-dialog-label">Full Name</span>
                <span className="user-management-view-dialog-value">
                  {selectedUserForView.full_name}
                </span>
              </div>
              <div className="user-management-view-dialog-row">
                <span className="user-management-view-dialog-label">Gender</span>
                <span className="user-management-view-dialog-value">
                  {selectedUserForView.gender || '—'}
                </span>
              </div>
              <div className="user-management-view-dialog-row">
                <span className="user-management-view-dialog-label">Date of Birth</span>
                <span className="user-management-view-dialog-value">
                  {selectedUserForView.date_of_birth || '—'}
                </span>
              </div>
              <div className="user-management-view-dialog-row">
                <span className="user-management-view-dialog-label">Email</span>
                <span className="user-management-view-dialog-value">
                  {selectedUserForView.email}
                </span>
              </div>
              <div className="user-management-view-dialog-row">
                <span className="user-management-view-dialog-label">Phone Number</span>
                <span className="user-management-view-dialog-value">
                  {selectedUserForView.mobile_number
                    ? `+91 ${selectedUserForView.mobile_number}`
                    : '—'}
                </span>
              </div>
              <div className="user-management-view-dialog-row">
                <span className="user-management-view-dialog-label">Username</span>
                <span className="user-management-view-dialog-value">
                  {selectedUserForView.username}
                </span>
              </div>
              <div className="user-management-view-dialog-row user-management-view-dialog-row-role">
                <span className="user-management-view-dialog-label">Role</span>
                <span className="user-management-view-dialog-value">
                  <span className="user-details-role-badge">{selectedUserForView.role}</span>
                </span>
              </div>
              <div className="user-management-view-dialog-row">
                <span className="user-management-view-dialog-label">Status</span>
                <div className="user-details-status-wrap">
                  {selectedUserForView.status === 'Active' ? (
                    <>
                      <CheckCircle
                        className="user-details-status-icon user-details-status-icon-active"
                        size={16}
                        strokeWidth={ICON_STROKE_WIDTH}
                      />
                      <span className="user-management-view-dialog-value">
                        {selectedUserForView.status}
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle
                        className="user-details-status-icon user-details-status-icon-inactive"
                        size={16}
                        strokeWidth={ICON_STROKE_WIDTH}
                      />
                      <span className="user-management-view-dialog-value">
                        {selectedUserForView.status}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {selectedUserForView.role !== 'ReferringPhysician' && (
                <div className="user-management-view-dialog-row">
                  <span className="user-management-view-dialog-label">Department</span>
                  <span className="user-management-view-dialog-value">
                    {formatDepartmentForUserView(selectedUserForView, formatSpecialty)}
                  </span>
                </div>
              )}
            </div>
            {/* PROFESSIONAL SECTION */}
            {selectedUserForView.role !== 'ReferringPhysician' && (
              <>
                <h3 className="user-details-section-title">Professional Credentials</h3>
                <div className="user-details-grid">
                  {selectedUserForView.role === 'Technician' ? (
                    <div className="user-details-shift-technician">
                      <div className="user-management-view-dialog-row user-details-shift-row">
                        <span className="user-management-view-dialog-label">Shift starts at</span>
                        <span className="user-management-view-dialog-value">
                          {formatTimeForDisplay(selectedUserForView.shift_start_at) || '—'}
                        </span>
                      </div>
                      <div className="user-management-view-dialog-row user-details-shift-row">
                        <span className="user-management-view-dialog-label">Shift ends at</span>
                        <span className="user-management-view-dialog-value">
                          {formatTimeForDisplay(selectedUserForView.shift_end_at) || '—'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="user-management-view-dialog-row">
                        <span className="user-management-view-dialog-label">Sub-role</span>
                        <span className="user-management-view-dialog-value">
                          {selectedUserForView.sub_role
                            ? (SUB_ROLE_OPTIONS.find(o => o.value === selectedUserForView.sub_role)
                                ?.label ?? selectedUserForView.sub_role)
                            : '—'}
                        </span>
                      </div>
                      <div className="user-management-view-dialog-row">
                        <span className="user-management-view-dialog-label">Designation</span>
                        <span className="user-management-view-dialog-value">
                          {selectedUserForView.designation || '—'}
                        </span>
                      </div>
                      <div className="user-management-view-dialog-row">
                        <span className="user-management-view-dialog-label">Registration No.</span>
                        <span className="user-management-view-dialog-value">
                          {selectedUserForView.registration_number || '—'}
                        </span>
                      </div>
                      <div className="user-management-view-dialog-row">
                        <span className="user-management-view-dialog-label">Qualification</span>
                        <span className="user-management-view-dialog-value">
                          {selectedUserForView.qualification || '—'}
                        </span>
                      </div>
                      <div className="user-management-view-dialog-row">
                        <span className="user-management-view-dialog-label">Experience</span>
                        <span className="user-management-view-dialog-value">
                          {selectedUserForView.experience_years != null
                            ? `${selectedUserForView.experience_years} years`
                            : '—'}
                        </span>
                      </div>
                      <div className="user-management-view-dialog-row">
                        <span className="user-management-view-dialog-label">Specialty</span>
                        <span className="user-management-view-dialog-value">
                          {formatSpecialty(selectedUserForView.specialty) || '—'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {/* REFERRING PHYSICIAN (UNCHANGED) */}
            {selectedUserForView.role === 'ReferringPhysician' && (
              <>
                <h3 className="user-details-section-title">Professional Information</h3>
                <div className="user-details-grid">
                  <div className="user-management-view-dialog-row">
                    <span className="user-management-view-dialog-label">Qualification</span>
                    <span className="user-management-view-dialog-value">
                      {selectedUserForView.qualification || '—'}
                    </span>
                  </div>
                  <div className="user-management-view-dialog-row">
                    <span className="user-management-view-dialog-label">Specialization</span>
                    <span className="user-management-view-dialog-value">
                      {formatSpecialty(selectedUserForView.specialty) || '—'}
                    </span>
                  </div>
                  <div className="user-management-view-dialog-row">
                    <span className="user-management-view-dialog-label">Registration Number</span>
                    <span className="user-management-view-dialog-value">
                      {selectedUserForView.registration_number || '—'}
                    </span>
                  </div>
                  <div className="user-management-view-dialog-row">
                    <span className="user-management-view-dialog-label">Clinic/Hospital Name</span>
                    <span className="user-management-view-dialog-value">
                      {selectedUserForView.clinic_hospital_name || '—'}
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* MODALITY + SIGNATURE (Radiologist only — not Technician) */}
            {selectedUserForView.role === 'Radiologist' && (
              <>
                <h3 className="user-details-section-title">Modality Access</h3>
                <div className="user-details-modality-pills">
                  {Array.isArray(selectedUserForView.reporting_modality_access) &&
                  selectedUserForView.reporting_modality_access.length > 0 ? (
                    selectedUserForView.reporting_modality_access.map(m => (
                      <span
                        key={m}
                        className="user-details-pill"
                      >
                        {m}
                      </span>
                    ))
                  ) : (
                    <span className="user-management-view-dialog-value">—</span>
                  )}
                </div>

                <div className="user-details-section-title-row">
                  <h3 className="user-details-section-title user-details-section-title--inline">
                    Signature
                  </h3>
                  {selectedUserForView.signature ? (
                    <button
                      type="button"
                      className="user-details-signature-preview-btn"
                      onClick={onPreviewSignature}
                      aria-label="Preview signature"
                      title="Preview signature"
                    >
                      <Eye
                        size={18}
                        strokeWidth={ICON_STROKE_WIDTH}
                        aria-hidden
                      />
                    </button>
                  ) : null}
                </div>
                <div className="user-management-view-dialog-row">
                  <span className="user-management-view-dialog-value">
                    {extractFilenameFromPath(selectedUserForView.signature) || '—'}
                  </span>
                </div>
              </>
            )}

            {/* AVAILABILITY — Radiologist only (not Technician / ReferringPhysician) */}
            {selectedUserForView.role === 'Radiologist' && (
              <>
                <h3 className="user-details-section-title">Availability</h3>
                <div className="user-details-grid">
                  <div className="user-management-view-dialog-row">
                    <span className="user-management-view-dialog-label">Availability Hours</span>
                    <span className="user-management-view-dialog-value">
                      {formatAvailabilityHoursForUserView(
                        selectedUserForView,
                        formatTimeForDisplay
                      )}
                    </span>
                  </div>

                  <div className="user-management-view-dialog-row">
                    <span className="user-management-view-dialog-label">Days</span>
                    <span className="user-management-view-dialog-value">
                      {Array.isArray(selectedUserForView.availability_days)
                        ? selectedUserForView.availability_days.join(', ')
                        : selectedUserForView.availability_days || '—'}
                    </span>
                  </div>
                </div>
              </>
            )}
            <h3 className="user-details-section-title">System Information</h3>
            <div className="user-details-grid">
              <div className="user-management-view-dialog-row">
                <span className="user-management-view-dialog-label">Created Date</span>
                <span className="user-management-view-dialog-value">
                  {formatDateTimeShort(
                    selectedUserForView.createdAt || selectedUserForView.created_at
                  ) || '—'}
                </span>
              </div>
              <div className="user-management-view-dialog-row">
                <span className="user-management-view-dialog-label">Created By</span>
                <span className="user-management-view-dialog-value">
                  {selectedUserForView.createdBy?.full_name || '—'}
                </span>
              </div>
              <div className="user-management-view-dialog-row">
                <span className="user-management-view-dialog-label">Last Modified</span>
                <span className="user-management-view-dialog-value">
                  {formatDateTimeShort(
                    selectedUserForView.updatedAt || selectedUserForView.updated_at
                  ) || '—'}
                </span>
              </div>
              <div className="user-management-view-dialog-row">
                <span className="user-management-view-dialog-label">Last Modified By</span>
                <span className="user-management-view-dialog-value">
                  {selectedUserForView.modifiedBy?.full_name || '—'}
                </span>
              </div>
            </div>
          </div>
          <div className="user-details-dialog-footer">
            <Button
              type="button"
              variant="secondary"
              fullWidth={false}
              className="user-details-btn user-details-btn-close"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              type="button"
              variant="primary"
              fullWidth={false}
              className="user-details-btn user-details-btn-edit"
              onClick={() => onEditFromView(selectedUserForView)}
            >
              <Edit
                className="user-details-btn-icon"
                size={16}
                strokeWidth={ICON_STROKE_WIDTH}
              />
              Edit User
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

function UserManagementViewUserDialog(
  props: Readonly<{
    open: boolean;
    user: User | null;
    onClose: () => void;
    formatSpecialty: (value: User['specialty']) => string;
    onPreviewSignature: () => void;
    onEditFromView: (user: User) => void;
  }>
) {
  const { open, user, onClose, formatSpecialty, onPreviewSignature, onEditFromView } = props;
  if (!open || !user) {
    return null;
  }
  return (
    <UserManagementViewUserDialogContent
      user={user}
      onClose={onClose}
      formatSpecialty={formatSpecialty}
      onPreviewSignature={onPreviewSignature}
      onEditFromView={onEditFromView}
    />
  );
}

function UserManagementListBody(
  props: Readonly<{
    isInitialLoad: boolean;
    usersLength: number;
    hasActiveFilters: boolean;
    handleAddUser: () => void;
    isFacilityScopedUsers: boolean;
    facilityNameFromNav: string;
    summaryStats: {
      total: number;
      active: number;
      radiologists: number;
      technicians: number;
      inactive: number;
    };
    searchQuery: string;
    setSearchQuery: (v: string) => void;
    setCurrentPage: (p: number) => void;
    roleFilter: string;
    statusFilter: string;
    createFilterHandler: (
      currentValue: string,
      setter: (value: string) => void
    ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    setRoleFilter: (v: string) => void;
    setStatusFilter: (v: string) => void;
    columns: TableColumn[];
    tableUsers: any[];
    handleToggleActive: (user: UserTableRow, index: number, checked: boolean) => void;
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
  }>
) {
  const {
    isInitialLoad,
    usersLength,
    hasActiveFilters,
    handleAddUser,
    isFacilityScopedUsers,
    facilityNameFromNav,
    summaryStats,
    searchQuery,
    setSearchQuery,
    setCurrentPage,
    roleFilter,
    statusFilter,
    createFilterHandler,
    setRoleFilter,
    setStatusFilter,
    columns,
    tableUsers,
    handleToggleActive,
    currentPage,
    totalItems,
    itemsPerPage,
  } = props;

  if (isInitialLoad && usersLength === 0) {
    return <div className="user-management-loading">Loading users...</div>;
  }
  if (usersLength === 0 && !hasActiveFilters) {
    return (
      <EmptyState
        title="No Users added yet"
        description=""
        actionLabel="Add Users"
        onAction={handleAddUser}
        illustrationType="empty-box"
      />
    );
  }

  return (
    <div className="user-management-container">
      <header className="user-management-subheader">
        <h1 className="user-management-subheader-title">
          {isFacilityScopedUsers ? facilityNameFromNav || 'Facility' : 'User Management'}
        </h1>
        <button
          type="button"
          onClick={handleAddUser}
          className="user-management-subheader-add-btn"
        >
          <Plus
            strokeWidth={ICON_STROKE_WIDTH}
            size={18}
          />
          Add User
        </button>
      </header>
      <div className="user-management-inner">
        <div className="user-management-summary-cards">
          <div className="um-summary-card">
            <span
              className="um-summary-icon um-summary-icon-users"
              aria-hidden
            >
              <Users
                size={20}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </span>
            <div>
              <div className="um-summary-value">{summaryStats.total}</div>
              <div className="um-summary-desc">{summaryStats.active} active</div>
            </div>
            <div className="um-summary-label">Total Users</div>
          </div>
          <div className="um-summary-card">
            <span
              className="um-summary-icon um-summary-icon-radiologist"
              aria-hidden
            >
              <Shield
                size={20}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </span>
            <div>
              <div className="um-summary-value">{summaryStats.radiologists}</div>
              <div className="um-summary-desc">Active clinical staff</div>
            </div>
            <div className="um-summary-label">Radiologists</div>
          </div>
          <div className="um-summary-card">
            <span
              className="um-summary-icon um-summary-icon-technologist"
              aria-hidden
            >
              <UserCog
                size={20}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </span>
            <div>
              <div className="um-summary-value">{summaryStats.technicians}</div>
              <div className="um-summary-desc">Imaging technologists</div>
            </div>
            <div className="um-summary-label">Technologists</div>
          </div>
          <div className="um-summary-card">
            <span
              className="um-summary-icon um-summary-icon-inactive"
              aria-hidden
            >
              <XCircle
                size={20}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </span>
            <div>
              <div className="um-summary-value">{summaryStats.inactive}</div>
              <div className="um-summary-desc">Deactivated accounts</div>
            </div>
            <div className="um-summary-label">Inactive Users</div>
          </div>
        </div>

        <div className="user-management-header user-management-filters-inline">
          <div className="user-management-header-left">
            <div className="user-management-filters">
              <div className="user-management-filter-wrapper user-management-filter-search">
                <span
                  className="user-management-search-icon"
                  aria-hidden
                >
                  <Search
                    size={18}
                    strokeWidth={ICON_STROKE_WIDTH}
                  />
                </span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search by name, email, phone, employee ID..."
                  className="user-management-search-input"
                  aria-label="Search by name, email, phone, or employee ID"
                />
              </div>
              <div className="user-management-filter-wrapper user-management-filter-wrapper-role">
                <FormSelectVirtualized
                  label=""
                  options={ROLE_OPTIONS}
                  value={roleFilter}
                  onChange={createFilterHandler(roleFilter, setRoleFilter)}
                  placeholder="All Roles"
                  name="roleFilter"
                />
              </div>
              <div className="user-management-filter-wrapper user-management-filter-wrapper-status">
                <FormSelectVirtualized
                  label=""
                  options={STATUS_OPTIONS}
                  value={statusFilter}
                  onChange={createFilterHandler(statusFilter, setStatusFilter)}
                  placeholder="All Statuses"
                  name="statusFilter"
                />
              </div>
            </div>
          </div>
          <div className="user-management-header-right" />
        </div>

        <div className="user-management-table-container">
          <DataTable
            columns={columns}
            data={tableUsers}
            onToggleActive={handleToggleActive}
            getRowKey={row => row.id}
            fixedRightColumnKey="action"
            pageScrollStickyHeader
          />
        </div>

        <div className="user-management-pagination">
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
}

const UserManagement: React.FC = () => {
  return <UserManagementInner />;
};

function UserManagementInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;

  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearchQuery = useDebounce(searchQuery.trim(), 400);
  const [currentPage, setCurrentPage] = useState(1);
  const [users, setUsers] = useState<User[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [pagination, setPagination] = useState<UserPaginationInfo | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [selectedUserForPasswordChange, setSelectedUserForPasswordChange] = useState<User | null>(
    null
  );
  const [actionMenuRowId, setActionMenuRowId] = useState<string | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<ActionMenuAnchor | null>(null);
  const actionMenuPortalRef = useRef<HTMLDivElement | null>(null);
  const [viewUserDialogOpen, setViewUserDialogOpen] = useState(false);
  const [selectedUserForView, setSelectedUserForView] = useState<User | null>(null);
  const [userFormDialogOpen, setUserFormDialogOpen] = useState(false);
  const [userFormDialogMode, setUserFormDialogMode] = useState<'add' | 'edit'>('add');
  const [userFormDialogInitialUser, setUserFormDialogInitialUser] = useState<User | null>(null);
  /** When set, closing/saving Edit dialog will reopen User Details with this user (opened Edit from View). */
  const [returnToViewUserOnClose, setReturnToViewUserOnClose] = useState<User | null>(null);
  const [viewSignaturePreviewOpen, setViewSignaturePreviewOpen] = useState(false);
  const [viewSignaturePreviewUrl, setViewSignaturePreviewUrl] = useState<string | null>(null);
  const [viewSignaturePreviewFileName, setViewSignaturePreviewFileName] = useState<string | null>(
    null
  );
  /** Total counts irrespective of pagination/filters (for summary cards) */
  const [summaryCounts, setSummaryCounts] = useState<{
    total: number;
    radiologists: number;
    technicians: number;
    inactive: number;
    active: number;
  } | null>(null);

  const formatSpecialty = formatUserSpecialty;

  const clearUsersAndFinishLoad = useCallback(() => {
    setUsers([]);
    setPagination(null);
    setSummaryCounts(null);
    setIsInitialLoad(false);
  }, []);

  /** Migrate legacy `?facilityId=` URLs to clean `/users` + in-memory state (no id in address bar). */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('facilityId')?.trim();
    if (!q) {
      return;
    }
    if (getUserRoleFromAuthUser(authService.getUser()) !== 'SuperAdmin') {
      return;
    }
    navigate('/users', { replace: true, state: { facilityId: q } });
  }, [location.search, navigate]);

  /** Facility scope for list + summary: Super Admin may open Users from Facility Management via `location.state`. */
  const facilityIdFromNav = useMemo(
    () => resolveFacilityIdFromNav(location.state),
    [location.state]
  );

  const facilityNameFromNav = useMemo(
    () => resolveFacilityNameFromNav(location.state),
    [location.state]
  );

  const isFacilityScopedUsers = useMemo(
    () => isFacilityScopedUsersList(facilityIdFromNav),
    [facilityIdFromNav]
  );

  const userManagementBreadcrumbs = useMemo(
    (): BreadcrumbItem[] => buildUserManagementBreadcrumbs(isFacilityScopedUsers),
    [isFacilityScopedUsers]
  );

  const usersListFacilityId = useMemo(
    () => resolveUsersListFacilityId(facilityIdFromNav),
    [facilityIdFromNav]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [facilityIdFromNav]);

  const fetchUsers = useCallback(async () => {
    try {
      const filters = buildUsersListFilters(roleFilter, statusFilter, debouncedSearchQuery);

      const response = await userService.getAllUsers(currentPage, usersListFacilityId, filters);

      if (!(response.success && response.data)) {
        const errorMessage = response.error || 'Failed to load users';
        clearUsersAndFinishLoad();
        notifyUserManagement(uiNotificationService, false, errorMessage);
        return;
      }

      setUsers(response.data);
      if (response.pagination) {
        setPagination(response.pagination);
      }
      setIsInitialLoad(false);
      await refreshUserSummaryCounts(usersListFacilityId, setSummaryCounts);
      if (response.message) {
        notifyUserManagement(uiNotificationService, true, response.message);
      }
    } catch (error) {
      console.error('UserManagement - Error fetching users:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load users';
      clearUsersAndFinishLoad();
      notifyUserManagement(uiNotificationService, false, errorMessage);
    }
  }, [
    currentPage,
    roleFilter,
    statusFilter,
    debouncedSearchQuery,
    usersListFacilityId,
    uiNotificationService,
    clearUsersAndFinishLoad,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Calculate pagination values from backend or use defaults
  const itemsPerPage = pagination?.usersPerPage || 10;
  const totalItems = pagination?.totalUsers || 0;

  const handleAddUser = () => {
    setUserFormDialogMode('add');
    setUserFormDialogInitialUser(null);
    setUserFormDialogOpen(true);
  };

  const handleEdit = useCallback(
    (user: UserTableRow) => {
      const originalUser = users.find(u => u.id?.toString() === user.id);
      if (originalUser) {
        setReturnToViewUserOnClose(null);
        setUserFormDialogMode('edit');
        setUserFormDialogInitialUser(originalUser);
        setUserFormDialogOpen(true);
      }
    },
    [users]
  );

  const openViewUserSignaturePreview = useCallback(() => {
    const user = selectedUserForView;
    if (!user?.signature) {
      return;
    }
    const url = resolveSignatureAssetUrl(user.signature);
    if (!url) {
      return;
    }
    setViewSignaturePreviewUrl(url);
    setViewSignaturePreviewFileName(extractFilenameFromPath(user.signature));
    setViewSignaturePreviewOpen(true);
  }, [selectedUserForView]);

  const handleViewSignaturePreviewOpenChange = useCallback((o: boolean) => {
    if (!o) {
      setViewSignaturePreviewUrl(null);
      setViewSignaturePreviewFileName(null);
    }
    setViewSignaturePreviewOpen(o);
  }, []);

  const handleUserFormSuccess = useCallback(
    async (user: User) => {
      await handleUserFormSuccessFlow({
        user,
        userFormDialogMode,
        returnToViewUserOnClose,
        uiNotificationService,
        fetchUsers,
        setUserFormDialogOpen,
        setUserFormDialogInitialUser,
        setSelectedUserForView,
        setViewUserDialogOpen,
        setReturnToViewUserOnClose,
      });
    },
    [userFormDialogMode, returnToViewUserOnClose, uiNotificationService, fetchUsers]
  );

  const handleToggleActive = async (user: UserTableRow, index: number, checked: boolean) => {
    await toggleUserActiveStatus({
      users,
      user,
      checked,
      setUsers,
      uiNotificationService,
      fetchUsers,
    });
  };

  const hasActiveFilters = Boolean(searchQuery.trim() || roleFilter || statusFilter);

  // Handler for FormSelectVirtualized onChange with toggle functionality
  const createFilterHandler = (currentValue: string, setter: (value: string) => void) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      // If clicking the same option, unselect it
      if (currentValue === newValue) {
        setter('');
      } else {
        setter(newValue);
      }
      setCurrentPage(1); // Reset to first page when filter changes
    };
  };

  const handleChangePassword = useCallback(
    (user: UserTableRow) => {
      const originalUser = users.find(u => u.id?.toString() === user.id);
      if (originalUser) {
        setSelectedUserForPasswordChange(originalUser);
        setChangePasswordModalOpen(true);
      }
    },
    [users]
  );

  const handlePasswordSubmit = useCallback(
    async (newPassword: string) => {
      if (!selectedUserForPasswordChange?.id) {
        throw new Error('User ID is required to change password');
      }

      const response = await authService.updatePassword(
        newPassword,
        selectedUserForPasswordChange.id
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to change password');
      }

      if (uiNotificationService) {
        uiNotificationService.show({
          title: 'Success',
          message: 'Password changed successfully',
          type: 'success',
          duration: 3000,
        });
      }
    },
    [selectedUserForPasswordChange, uiNotificationService]
  );

  const handleCloseActionMenu = useCallback(() => {
    setActionMenuRowId(null);
    setActionMenuAnchor(null);
  }, []);

  useEffect(() => {
    if (!actionMenuRowId) {
      setActionMenuAnchor(null);
      return;
    }
    const rafId = requestAnimationFrame(() => {
      const trigger = document.querySelector(
        `[data-action-trigger-row-id="${actionMenuRowId}"]`
      ) as HTMLElement | null;
      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        setActionMenuAnchor({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
        });
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [actionMenuRowId]);

  useEffect(() => {
    const el = actionMenuPortalRef.current;
    if (!el || !actionMenuAnchor) {
      return;
    }
    el.style.setProperty('--um-action-menu-top', `${actionMenuAnchor.top}px`);
    el.style.setProperty('--um-action-menu-right', `${actionMenuAnchor.right}px`);
  }, [actionMenuAnchor]);

  const handleViewUser = useCallback((user: User) => {
    setSelectedUserForView(user);
    setViewUserDialogOpen(true);
  }, []);

  // Map users to table format (search, role, status filtered server-side)
  const tableUsers = useMemo(() => {
    return users.map((user, index) => {
      const tableData = mapApiResponseToTableFormat(user);
      const rowId = String(user.id ?? index);
      const isMenuOpen = actionMenuRowId === rowId;
      const rowForHandler = {
        ...tableData,
        slNo: (currentPage - 1) * itemsPerPage + index + 1,
        active: user.status === 'Active',
      };
      return {
        ...tableData,
        slNo: (currentPage - 1) * itemsPerPage + index + 1,
        active: user.status === 'Active',
        action: (
          <div
            className="user-management-actions-cell"
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              className="user-management-action-btn"
              onClick={e => {
                e.stopPropagation();
                handleViewUser(user);
              }}
              aria-label="View user"
              title="View user info"
            >
              <Eye
                className="user-management-action-btn-icon"
                size={18}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </button>
            <button
              type="button"
              className="user-management-action-btn"
              onClick={e => {
                e.stopPropagation();
                handleEdit(rowForHandler, index);
              }}
              aria-label="Edit user"
              title="Edit"
            >
              <Edit
                className="user-management-action-btn-icon"
                size={18}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </button>
            <div className="user-management-action-cell">
              <button
                type="button"
                className="user-management-action-trigger"
                data-action-trigger-row-id={rowId}
                onClick={e => {
                  e.stopPropagation();
                  setActionMenuRowId(actionMenuRowId === rowId ? null : rowId);
                }}
                aria-label="More actions"
                aria-expanded={isMenuOpen}
              >
                <MoreVertical
                  className="user-management-action-icon"
                  size={20}
                  strokeWidth={ICON_STROKE_WIDTH}
                />
              </button>
            </div>
          </div>
        ),
      };
    });
  }, [users, currentPage, itemsPerPage, handleEdit, handleViewUser, actionMenuRowId]);

  const columns: TableColumn<UserTableRow>[] = [
    { key: 'slNo', label: 'Sl.No', width: '54px', align: 'left' },
    { key: 'active', label: 'Active', width: '80px', align: 'center' },
    { key: 'fullName', label: 'Employee Name', width: '150px' },
    { key: 'employeeId', label: 'Employee ID', width: '120px' },
    { key: 'phoneNo', label: 'Phone number', width: '140px' },
    { key: 'role', label: 'Role', width: '120px' },
    { key: 'emailId', label: 'Email ID', width: '200px' },
    { key: 'username', label: 'Username', width: '140px' },
    { key: 'createdBy', label: 'Created By', width: '120px' },
    { key: 'createdAt', label: 'Created at', width: '150px' },
    { key: 'lastModifiedBy', label: 'Last Modified By', width: '150px' },
    { key: 'lastModified', label: 'Last Modified', width: '150px' },
    { key: 'action', label: 'Actions', width: '140px', align: 'center' },
  ];

  const summaryStats = useMemo(
    () => resolveSummaryStats(summaryCounts, users),
    [summaryCounts, users]
  );

  return (
    <PageLayout
      title={isFacilityScopedUsers ? facilityNameFromNav || 'Facility' : 'User Management'}
      hideTitleInHeader
      breadcrumbs={userManagementBreadcrumbs}
      onBack={isFacilityScopedUsers ? () => navigate('/facilities') : undefined}
    >
      <UserManagementListBody
        isInitialLoad={isInitialLoad}
        usersLength={users.length}
        hasActiveFilters={hasActiveFilters}
        handleAddUser={handleAddUser}
        isFacilityScopedUsers={isFacilityScopedUsers}
        facilityNameFromNav={facilityNameFromNav}
        summaryStats={summaryStats}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        setCurrentPage={setCurrentPage}
        roleFilter={roleFilter}
        statusFilter={statusFilter}
        createFilterHandler={createFilterHandler}
        setRoleFilter={setRoleFilter}
        setStatusFilter={setStatusFilter}
        columns={columns}
        tableUsers={tableUsers}
        handleToggleActive={handleToggleActive}
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
      />

      <ChangePasswordModal
        isOpen={changePasswordModalOpen}
        onClose={() => {
          setChangePasswordModalOpen(false);
          setSelectedUserForPasswordChange(null);
        }}
        onSubmit={handlePasswordSubmit}
        title="Change Password"
      />

      <UserFormDialog
        open={userFormDialogOpen}
        mode={userFormDialogMode}
        initialUser={userFormDialogInitialUser}
        facilityId={usersListFacilityId ?? authService.getFacilityId()}
        onClose={() => {
          setUserFormDialogOpen(false);
          setUserFormDialogInitialUser(null);
          if (returnToViewUserOnClose) {
            setSelectedUserForView(returnToViewUserOnClose);
            setViewUserDialogOpen(true);
            setReturnToViewUserOnClose(null);
          }
        }}
        onSuccess={handleUserFormSuccess}
      />
      <SignaturePreviewDialog
        open={viewSignaturePreviewOpen}
        onOpenChange={handleViewSignaturePreviewOpenChange}
        sourceUrl={viewSignaturePreviewUrl}
        sourceUrlOwnedByParent={false}
        fileName={viewSignaturePreviewFileName}
      />
      <UserManagementViewUserDialog
        open={viewUserDialogOpen}
        user={selectedUserForView}
        onClose={() => {
          setViewUserDialogOpen(false);
          setSelectedUserForView(null);
        }}
        formatSpecialty={formatSpecialty}
        onPreviewSignature={openViewUserSignaturePreview}
        onEditFromView={user => {
          setReturnToViewUserOnClose(user);
          setViewUserDialogOpen(false);
          setUserFormDialogMode('edit');
          setUserFormDialogInitialUser(user);
          setUserFormDialogOpen(true);
        }}
      />
      {actionMenuRowId != null &&
        actionMenuAnchor != null &&
        createPortal(
          <>
            <div
              className="user-management-action-backdrop user-management-action-backdrop-portal"
              onClick={handleCloseActionMenu}
              aria-hidden
            />
            <div
              ref={actionMenuPortalRef}
              className="user-management-action-menu user-management-action-menu-portal"
            >
              <button
                type="button"
                className="user-management-action-menu-item"
                onClick={e => {
                  e.stopPropagation();
                  const openUser = users.find(u => String(u.id) === actionMenuRowId);
                  const openIndex = users.findIndex(u => String(u.id) === actionMenuRowId);
                  if (openUser && openIndex >= 0) {
                    const rowForHandler = {
                      ...mapApiResponseToTableFormat(openUser),
                      slNo: (currentPage - 1) * itemsPerPage + openIndex + 1,
                      active: openUser.status === 'Active',
                    };
                    handleCloseActionMenu();
                    handleChangePassword(rowForHandler);
                  }
                }}
              >
                Change Password
              </button>
            </div>
          </>,
          document.body
        )}
    </PageLayout>
  );
}

export default UserManagement;
